import type { AxiosResponse } from 'axios'

interface CommonRequestPayload {
  forceFetch?: boolean
  _urlParam?: string | number
  params?: Record<string, number | string | boolean>
}

interface FetchAudiencePayload extends CommonRequestPayload {
  params: {
    $top?: number
    $skip?: number
    $orderby?: string
    $audienceFilter?: string
    $select?: string
    $count?: boolean
  }
}

interface AudienceActionPayload extends CommonRequestPayload {
  body: {
    fanOids?: string[]
    filters?: string[]
    updates?: any
  }
}

type WithPromoterOid<T> = { promoterOid: number } & T

export interface ApiEndpoint<TPayload = CommonRequestPayload, TApiResponse = any> {
  url: string | ((payload: WithPromoterOid<TPayload>) => string)
  method?: string
  storeKey?: string
  cache?: boolean
  cacheTtl?: number
  loadingKey?: string
  resetCache?: string[]
  mutations?: string[]
  errorMessage?: string | ((payload: TPayload, error: AxiosResponse) => string)
  successMessage?: string | ((payload: TPayload, response: AxiosResponse<TPayload>) => string)
  successMessageTitle?: string | ((payload: TPayload, response: AxiosResponse<TPayload>) => string)
}

// FIXME: this should be generated
interface FanAccount {
  oid: string
}

type Counted<T> = {
  count: number
  rows: T[]
}

interface PromoterExternalApiSetting {
  oid: number
  promoterOid: number
  whitelistIpAddresses: string[]
  associatedMessageListOid?: number | null
  associatedMessageListOids?: number[]
  webhookUrl?: string | null
  defaultDynamicTags: string[]
  useExternalIdAsPk?: boolean
  sysCtime: Date
  sysMtime: Date
}

// FIXME: this should be generated
interface Tag {
  oid: number
  name: string
  count: number
  favourite: boolean
  external: boolean
}

interface FetchTagsPayload extends CommonRequestPayload {
  params: {
    $top?: number | 'all'
    $skip?: number
    $orderby?: string
    $select?: string
    $count?: boolean
    $filter?: string
  }
}

interface TagActionPayload extends CommonRequestPayload {
  body: {
    name?: string
    favourite?: boolean
    external?: boolean
  }
}

interface TagPatchPayload extends CommonRequestPayload {
  _urlParam: number
  body: {
    name?: string
    favourite?: boolean
  }
}

interface TagDeletePayload extends CommonRequestPayload {
  _urlParam: number
}

interface TagMergePayload extends CommonRequestPayload {
  _urlParam: number
  body: {
    srcTagOids: number[]
  }
}

interface TagGroupPayload extends CommonRequestPayload {
  _urlParam: number
  body: {
    tagOids: number[]
  }
}

// ==========================================
// ORDERS/EVENTS MODULE
// ==========================================

interface EventOrder {
  oid: number
  orderId: string
  firstName?: string
  lastName?: string
  fanPromoterAccount?: {
    firstName?: string
    lastName?: string
    emailAddress?: string
    oid: number
  }
  totalTickets: number
  ticketType?: string
  attribution?: 'email' | 'sms'
  status: string
  value: number // order total in cents
  orderCosts?: any[]
  provider?: string
  orderDate: string
}

interface FetchEventOrdersPayload extends CommonRequestPayload {
  eventOid: number // eventOid for URL substitution
  params: {
    $top?: number
    $skip?: number
    $orderby?: string
    $select?: string
    $filter?: string
    $count?: boolean
  }
}

interface FetchEventGroupOrdersPayload extends CommonRequestPayload {
  groupOid: number // groupOid for URL substitution
  body: {
    'event-oids': number[]
    '$search'?: string
    '$status'?: string
    '$top'?: number
    '$skip'?: number
    '$orderby'?: string
    '$select'?: string
    '$count'?: boolean
  }
}

interface FetchEventRevenueByMessagesPayload extends CommonRequestPayload {
  eventOid: number // eventOid for URL substitution
}

interface FetchMessageAttributionPayload extends CommonRequestPayload {
  params: {
    sourceType: string
    sourceOid: number
  }
}

interface AttributionIndicators {
  revenue: number
  totalOrders: number
}

export interface AttributionRecord {
  timeWindow: number
  indicators: AttributionIndicators
}

export interface AttributionApiResponse {
  astp: AttributionRecord[]
  status: string
}

interface EventRevenueByMessages {
  email: {
    count: number
    revenue: number
  }
  sms: {
    count: number
    revenue: number
  }
}

interface FetchEventGroupEventsPayload extends CommonRequestPayload {
  groupOid: number // groupOid for URL substitution
}

interface GetBatchTicketStatsPayload extends CommonRequestPayload {
  groupOid: number // groupOid for URL substitution
  body: {
    eventOids: number[]
    $audienceFilter?: string
  }
}

interface FetchShortUrlsPayload extends CommonRequestPayload {
  params: {
    promoterOid?: number
    taskOid?: number
    $top?: number
    $orderby?: string
    $filter?: string
  }
}

const API_ENDPOINTS = {
  // ==========================================
  // AUDIENCE MODULE
  // ==========================================
  fetchAudience: {
    url: '/promoter/:promoterOid/filter-fan/',
    method: 'GET',
    storeKey: 'audienceModule/audience',
    loadingKey: 'audienceModule/isFetchingAudience',
    errorMessage: 'Failed to fetch audience contacts. Please try again!',
  } satisfies ApiEndpoint<FetchAudiencePayload, FanAccount[]>,

  fetchAudienceCount: {
    url: '/promoter/:promoterOid/filter-fan/',
    method: 'GET',
    loadingKey: 'audienceModule/isFetchingAudienceCount',
    mutations: ['audienceModule/SET_AUDIENCE_COUNT'],
    errorMessage: 'Failed to fetch audience count. Please try again.',
  } satisfies ApiEndpoint<FetchAudiencePayload, Counted<FanAccount>>,

  massEditFans: {
    url: '/promoter/:promoterOid/mass-edit-fan',
    method: 'POST',
    errorMessage: payload => `Failed to update ${payload?.body?.fanOids?.length || 'selected'} contacts. Please try again.`,
  } satisfies ApiEndpoint<AudienceActionPayload>,

  massDeleteFans: {
    url: '/promoter/:promoterOid/mass-delete-fan',
    method: 'POST',
    resetCache: ['audienceModule/audience'],
    errorMessage: payload => `Failed to delete ${payload?.body?.fanOids?.length || 'selected'} contacts. Please try again.`,
    successMessage: payload => `Successfully deleted ${payload?.body?.fanOids?.length || 'selected'} contacts.`,
  } satisfies ApiEndpoint<AudienceActionPayload>,

  exportAudienceCsv: {
    url: '/promoter/:promoterOid/filter-fan-export/',
    method: 'POST',
    loadingKey: 'audienceModule/isExportingAudienceCsv',
    successMessage: (_, res) => res.status === 202 ? res.data : null,
    successMessageTitle: 'Export started',
    errorMessage: 'Failed to export audience CSV. Please try again.',
  } satisfies ApiEndpoint<AudienceActionPayload, { data: string, status: number }>,

  fetchAudienceOptInsStats: {
    url: '/promoter/:promoterOid/filter-fan/',
    method: 'GET',
    loadingKey: 'audienceModule/isFetchingAudienceOptInsStat',
    errorMessage: 'Failed to fetch audience contacts. Please try again!',
  } satisfies ApiEndpoint<FetchAudiencePayload, { count: number }>,

  deleteAudience: {
    url: '/promoter/:promoterOid/mass-delete-fan',
    method: 'POST',
    mutations: ['audienceModule/audience', 'audienceModule/audienceCount', 'audienceModule/promoterAudienceCount'],
    successMessage: payload => `Successfully deleted ${payload?.body.fanOids?.length || 'selected'} contacts.`,
    errorMessage: payload => `Failed to delete ${payload?.body.fanOids?.length || 'selected'} contacts. Please try again.`,
  } satisfies ApiEndpoint<AudienceActionPayload>,

  importOneSchemaCSV: {
    url: '/promoter/:promoterOid/oneschema-import/',
    method: 'POST',
    loadingKey: 'audienceModule/isImportingAudienceCsv',
    successMessage: 'Import has started. Your contacts will be added soon',
    errorMessage: (_, error: { message: { details: string } }) => error?.message?.details || error,
  } satisfies ApiEndpoint<AudienceActionPayload>,

  fetchPromoterExternalApiSettings: {
    url: '/promoter/:promoterOid/external-api-settings',
    method: 'GET',
    errorMessage: 'Failed to fetch promoter external API settings. Please try again.',
  } satisfies ApiEndpoint<CommonRequestPayload, PromoterExternalApiSetting[]>,

  createContact: {
    url: '/promoter/:promoterOid/mass-add-fan',
    method: 'POST',
    loadingKey: 'audienceModule/isImportingAudienceCsv',
    errorMessage: (_, error) => error?.status === 409 ? 'Duplicate error: a contact with this email already exists.' : 'Failed to update contact. Please try again.',
    successMessage: `Contact updated successfully`,
  } satisfies ApiEndpoint<AudienceActionPayload>,

  // ==========================================
  // CUSTOM CALENDAR ENTRY MODULE
  // ==========================================

  fetchCustomCalendarEntries: {
    url: '/promoter/:promoterOid/custom-calendar-entry',
    method: 'GET',
    loadingKey: 'customCalendarEntryModule/isFetching',
    errorMessage: 'Failed to fetch calendar entries. Please try again!',
  } satisfies ApiEndpoint,

  createCustomCalendarEntry: {
    url: '/promoter/:promoterOid/custom-calendar-entry',
    method: 'POST',
    loadingKey: 'customCalendarEntryModule/isCreating',
    successMessage: 'Calendar entry created',
    errorMessage: 'Failed to create calendar entry. Please try again!',
  } satisfies ApiEndpoint,

  updateCustomCalendarEntry: {
    url: '/promoter/:promoterOid/custom-calendar-entry/:entryOid',
    method: 'PATCH',
    successMessage: 'Calendar entry updated',
    errorMessage: 'Failed to update calendar entry. Please try again!',
  } satisfies ApiEndpoint,

  deleteCustomCalendarEntry: {
    url: '/promoter/:promoterOid/custom-calendar-entry/:entryOid',
    method: 'DELETE',
    loadingKey: 'customCalendarEntryModule/isDeleting',
    successMessage: 'Calendar entry deleted',
    errorMessage: 'Failed to delete calendar entry. Please try again!',
  } satisfies ApiEndpoint,

  // ==========================================
  // TAG MODULE
  // ==========================================

  fetchTags: {
    url: '/promoter/:promoterOid/tag',
    method: 'GET',
    cache: true,
    cacheTtl: 60,
    loadingKey: 'tagModule/isFetchingTags',
    mutations: ['tagModule/SET_TAGS', 'tagModule/SET_TAGS_COUNT', 'tagModule/SET_IS_NO_MORE_TAGS'],
    errorMessage: 'Failed to fetch tags. Please try again!',
  } satisfies ApiEndpoint<FetchTagsPayload, Counted<Tag>>,

  createTag: {
    url: '/promoter/:promoterOid/tag',
    method: 'POST',
    loadingKey: 'tagModule/isCreatingTag',
    mutations: ['tagModule/CONCAT_TAGS'],
    resetCache: ['tagModule/tags'],
    successMessage: 'Successfully created tag',
    errorMessage: 'Failed to create tag. Please try again!',
  } satisfies ApiEndpoint<TagActionPayload, Tag>,

  patchTag: {
    url: '/promoter/:promoterOid/tag/:tagOid',
    method: 'PATCH',
    mutations: ['tagModule/PATCH_TAG_IN_TAGS'],
    resetCache: ['tagModule/tags'],
    successMessage: 'Successfully updated tag',
    errorMessage: 'Failed to update tag. Please try again!',
  } satisfies ApiEndpoint<TagPatchPayload, Tag>,

  deleteTag: {
    url: '/promoter/:promoterOid/tag/:tagOid',
    method: 'DELETE',
    loadingKey: 'tagModule/isDeletingTag',
    mutations: ['tagModule/REMOVE_TAG_FROM_TAGS'],
    resetCache: ['tagModule/tags'],
    successMessage: 'Successfully deleted tag',
    errorMessage: 'Failed to delete tag. Please try again!',
  } satisfies ApiEndpoint<TagDeletePayload>,

  mergeTags: {
    url: '/promoter/:promoterOid/tag/:tagOid/merge',
    method: 'POST',
    loadingKey: 'tagModule/isMergingTags',
    resetCache: ['tagModule/tags'],
    successMessage: 'Successfully merged tags',
    errorMessage: 'Failed to merge tags. Please try again!',
  } satisfies ApiEndpoint<TagMergePayload>,

  groupTags: {
    url: '/promoter/:promoterOid/tag/:tagOid/group',
    method: 'POST',
    loadingKey: 'tagModule/isGroupingTags',
    resetCache: ['tagModule/tags'],
    successMessage: 'Successfully grouped tags',
    errorMessage: 'Failed to group tags. Please try again!',
  } satisfies ApiEndpoint<TagGroupPayload>,

  // ==========================================
  // EVENTS/ORDERS MODULE
  // ==========================================

  fetchEventOrders: {
    url: '/promoter/:promoterOid/event/:eventOid/order',
    method: 'GET',
    loadingKey: 'eventModule/isFetchingEventOrders',
    errorMessage: 'Failed to fetch event orders. Please try again!',
  } satisfies ApiEndpoint<FetchEventOrdersPayload, EventOrder[]>,

  fetchEventGroupOrders: {
    url: '/promoter/:promoterOid/event-group/:groupOid/order',
    method: 'POST',
    loadingKey: 'eventModule/isFetchingEventOrders',
    errorMessage: 'Failed to fetch event group orders. Please try again!',
  } satisfies ApiEndpoint<FetchEventGroupOrdersPayload, EventOrder[]>,

  fetchEventRevenueByMessages: {
    url: '/promoter/:promoterOid/event/:eventOid/revenue-event-by-messages',
    method: 'GET',
    errorMessage: 'Failed to fetch event revenue attribution. Please try again!',
  } satisfies ApiEndpoint<FetchEventRevenueByMessagesPayload, EventRevenueByMessages>,

  fetchEventGroupEvents: {
    url: '/promoter/:promoterOid/event-group/:groupOid/event',
    method: 'GET',
    loadingKey: 'eventModule/isFetchingEvents',
    errorMessage: 'Failed to fetch event group events. Please try again!',
  } satisfies ApiEndpoint<FetchEventGroupEventsPayload, any[]>,

  getBatchTicketStats: {
    url: '/promoter/:promoterOid/event-group/:groupOid/batch-events-ticket-sales-stats',
    method: 'POST',
    errorMessage: 'Failed to fetch batch ticket stats. Please try again!',
  } satisfies ApiEndpoint<GetBatchTicketStatsPayload, { results: any[] }>,

  getBatchRevenueByMessages: {
    url: '/promoter/:promoterOid/event-group/:groupOid/batch-events-revenue-by-messages',
    method: 'POST',
    errorMessage: 'Failed to fetch batch revenue attribution. Please try again!',
  } satisfies ApiEndpoint<GetBatchTicketStatsPayload, { results: any[] }>,

  fetchEventGroups: {
    url: '/promoter/:promoterOid/event-group',
    method: 'GET',
    errorMessage: 'Failed to fetch event groups. Please try again!',
  } satisfies ApiEndpoint<CommonRequestPayload, any[]>,

  fetchMessageAttribution: {
    url: '/promoter/:promoterOid/attribution',
    method: 'GET',
    errorMessage: 'Failed to fetch attribution data. Please try again!',
  } satisfies ApiEndpoint<FetchMessageAttributionPayload, AttributionApiResponse>,

  // ==========================================
  // SHORT URL MODULE
  // ==========================================

  fetchShortUrls: {
    url: '/promoter/:promoterOid/short-url',
    method: 'GET',
    loadingKey: 'shortUrlModule/isFetchingShortUrls',
    errorMessage: 'Failed to fetch short URLs. Please try again!',
  } satisfies ApiEndpoint<FetchShortUrlsPayload>,
}

export type EndpointKey = keyof typeof API_ENDPOINTS
export default API_ENDPOINTS
